// src/components/PermisosUsuarios.jsx
import React, { useState, useContext, useMemo, useEffect } from "react";
import Modal from "react-modal";
import { db, auth, functions } from "../firebaseConfig";
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  query,
  collection,
  where,
  getDocs,
} from "firebase/firestore";
import Swal from "sweetalert2";
import { httpsCallable } from "firebase/functions";
import { UsuariosContext } from "../context/UsuariosContext";
import { useAuth } from "../context/AuthContext";
import Paginador from "./Paginador";
import Spinner from "./Spinner";
import { useTranslation } from "react-i18next";

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    border: "none",
    borderRadius: "8px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    padding: "20px",
    maxWidth: "500px",
    width: "100%",
    height: "400px",
    maxHeight: "90vh",
    overflow: "auto",
    fontFamily: "Inter, sans-serif",
  },
  label: { display: "block", marginBottom: "10px" },
  input: {
    width: "100%",
    padding: "8px",
    marginBottom: "20px",
    borderRadius: "5px",
    border: "1px solid #ccc",
    boxSizing: "border-box",
  },
  button: {
    width: "50%",
    padding: "10px",
    borderRadius: "5px",
    backgroundColor: "#007bff",
    color: "#fff",
    border: "none",
    cursor: "pointer",
    fontSize: "16px",
    display: "flex",
    justifyContent: "center",
    margin: "0 auto",
    fontWeight: "700",
  },
  overlay: { backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 1008 },
};

export default function PermisosUsuarios() {
  const { t } = useTranslation();
  const { usuarios = [], loading: loadingCtx } = useContext(UsuariosContext);
  const { user, teamOwnerId, userRole } = useAuth();
  const ownerUid = teamOwnerId || user.uid;

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [nuevoUsuario, setNuevoUsuario] = useState({
    nombre: "",
    apellido: "",
    email: "",
    rol: "Ventas", // <- valor real que guardas
    id: null,
  });
  const [isSaving, setIsSaving] = useState(false);

  /* ------------------------- loading spinner ------------------------- */
  const [showSpinner, setShowSpinner] = useState(true);
  useEffect(() => {
    if (!loadingCtx) setShowSpinner(false);
  }, [loadingCtx]);

  /* ------------------------- tabla paginada -------------------------- */
  const usuariosUnicos = useMemo(() => {
    const seen = new Set();
    return usuarios.reduce((acc, u) => {
      const emailNorm = (u.email || "").trim().toLowerCase();
      if (emailNorm && !seen.has(emailNorm)) {
        seen.add(emailNorm);
        acc.push({ ...u, email: emailNorm });
      }
      return acc;
    }, []);
  }, [usuarios]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const totalUsuarios = usuariosUnicos.length;
  const paginatedUsuarios = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return usuariosUnicos.slice(start, start + itemsPerPage);
  }, [usuariosUnicos, currentPage, itemsPerPage]);

  /* ----------------------------- modal ------------------------------ */
  const handleOpenModal = () => setModalIsOpen(true);
  const handleCloseModal = () => {
    setModalIsOpen(false);
    setNuevoUsuario({
      nombre: "",
      apellido: "",
      email: "",
      rol: "Ventas",
      id: null,
    });
  };
  const handleInputChange = (e) => {
    setNuevoUsuario((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  /* ------------------------- guardar / invitar ------------------------- */
  const handleSaveUser = async () => {
    if (isSaving) return;

    const { nombre, apellido, email, rol, id } = nuevoUsuario;
    if (!nombre.trim() || !email.trim()) {
      Swal.fire(
        t("users.alerts.missing.title", "Missing data"),
        t("users.alerts.missing.body", "Please enter name and email."),
        "error"
      );
      return;
    }

    setIsSaving(true);
    const emailNorm = email.trim().toLowerCase();

    try {
      const invitacionRef = doc(db, "usuarios", ownerUid, "equipo", emailNorm);
      const invitacionSnap = await getDoc(invitacionRef);

      // sincroniza rol si el usuario raíz ya existe
      const syncRootRole = async () => {
        const q = query(
          collection(db, "usuarios"),
          where("email", "==", emailNorm)
        );
        const raizSnap = await getDocs(q);
        raizSnap.forEach((uDoc) => updateDoc(uDoc.ref, { rol }));
      };

      if (id) {
        // editar
        await updateDoc(invitacionRef, {
          nombre,
          apellido,
          email: emailNorm,
          rol,
        });
        await syncRootRole();
        Swal.fire(t("users.alerts.edited", "User updated!"), "", "success");
      } else if (invitacionSnap.exists()) {
        // placeholder existente
        await updateDoc(invitacionRef, { nombre, apellido, rol });
        await syncRootRole();
        Swal.fire(t("users.alerts.updated", "User updated!"), "", "success");
      } else {
        // nueva invitación
        await setDoc(invitacionRef, {
          nombre,
          apellido,
          email: emailNorm,
          rol,
          registrado: false,
          ownerId: ownerUid,
          invitadoPor: auth.currentUser.displayName || "",
          creadoEl: new Date(),
        });

        // correo de invitación por Cloud Function
        const sendInvite = httpsCallable(functions, "sendCustomInvite");
        await sendInvite({
          email: emailNorm,
          ownerUid,
          displayName: `${nombre} ${apellido}`.trim(),
          rol,
        });

        Swal.fire(
          t("users.alerts.invited.title", "Invitation sent!"),
          t("users.alerts.invited.body", "Check the user's email."),
          "success"
        );
      }

      handleCloseModal();
    } catch (err) {
      console.error("Error al guardar usuario:", err);
      Swal.fire(
        t("users.alerts.error.title", "Error"),
        t("users.alerts.error.body", "Could not save"),
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  /* --------------------------- editar / borrar --------------------------- */
  const handleEditUser = (u) => {
    setNuevoUsuario({
      nombre: u.nombre || "",
      apellido: u.apellido || "",
      email: u.email || "",
      rol: u.rol || "Ventas",
      id: u.id,
    });
    handleOpenModal();
  };

  const handleDeleteUser = async (u) => {
    const { isConfirmed } = await Swal.fire({
      title: t("users.confirm.delete.title", "Delete user?"),
      text: u.email,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("users.confirm.delete.confirm", "Yes, delete"),
      cancelButtonText: t("actions.cancel", "Cancel"),
    });

    if (!isConfirmed) return;
    await deleteDoc(doc(db, "usuarios", ownerUid, "equipo", u.id));
    Swal.fire(t("users.alerts.deleted", "User deleted!"), "", "success");
  };

  /* ------------------------------ render ------------------------------ */
  if (showSpinner) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <div className="no-sticky">
        {/* Encabezado */}
        <div className="users-toolbar">
          <div className="users-title">
            <h4>{t("users.all", "All users")}</h4>
            <i className="bi bi-chevron-down" style={{ color: "#007bff" }} />
          </div>

          {userRole === "Administrador" && (
            <button className="new-task-button" onClick={handleOpenModal}>
              {t("users.inviteButton", "Invite a user")}
            </button>
          )}
        </div>

        {/* Tabla + modal + paginación */}
        <div className="cotizaciones-table">
          <div className="tabla-cotizaciones-wrapper">
            <div className="tabla-cotizaciones-content">
              <table>
                <thead>
                  <tr>
                    <th>{t("users.table.name", "NAME")}</th>
                    <th>{t("users.table.email", "EMAIL")}</th>
                    <th>{t("users.table.role", "ROLE")}</th>
                    <th>{t("users.table.status", "STATUS")}</th>
                    <th>{t("users.table.options", "OPTIONS")}</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedUsuarios.map((u) => (
                    <tr key={u.id}>
                      <td>
                        {u.nombre} {u.apellido}
                      </td>
                      <td>{u.email}</td>
                      <td>
                        {/* Muestra el rol traducido, pero mantén el valor guardado en español */}
                        {t(`roles.${u.rol}`, u.rol)}
                      </td>
                      <td style={{ color: u.registrado ? "green" : "red" }}>
                        {u.registrado
                          ? t("users.status.registered", "Registered")
                          : t("users.status.unregistered", "Not Registered")}
                      </td>
                      <td
                        style={{
                          width: 80,
                          textAlign: "center",
                          padding: "4px 2px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <i
                          className="bi bi-pencil-fill"
                          style={{ cursor: "pointer", marginRight: 8 }}
                          title={t("users.tooltips.edit", "Edit user")}
                          onClick={() => handleEditUser(u)}
                        />
                        <i
                          className="bi bi-trash-fill"
                          style={{ cursor: "pointer", color: "#dc3545" }}
                          title={t("users.tooltips.delete", "Delete user")}
                          onClick={() => handleDeleteUser(u)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Modal */}
              <Modal
                isOpen={modalIsOpen}
                onRequestClose={handleCloseModal}
                style={customStyles}
                ariaHideApp={false}
              >
                <button onClick={handleCloseModal} className="modal-close">
                  <i className="bi bi-x-lg"></i>
                </button>
                <h2 style={{ textAlign: "center", marginBottom: "20px" }}>
                  {nuevoUsuario.id
                    ? t("users.modal.editTitle", "Edit User")
                    : t("users.modal.inviteTitle", "Invite User")}
                </h2>

                <div style={{ marginTop: "40px" }}>
                  <div
                    style={{
                      display: "flex",
                      marginBottom: "15px",
                      gap: "10px",
                    }}
                  >
                    <input
                      type="text"
                      name="nombre"
                      placeholder={t("users.form.firstName", "First name")}
                      value={nuevoUsuario.nombre}
                      onChange={handleInputChange}
                      style={customStyles.input}
                    />
                    <input
                      type="text"
                      name="apellido"
                      placeholder={t("users.form.lastName", "Last name")}
                      value={nuevoUsuario.apellido}
                      onChange={handleInputChange}
                      style={customStyles.input}
                    />
                  </div>

                  <input
                    type="email"
                    name="email"
                    placeholder={t("users.form.email", "Email")}
                    value={nuevoUsuario.email}
                    onChange={handleInputChange}
                    style={customStyles.input}
                  />

                  <label style={customStyles.label}>
                    {t("users.form.role", "Role")}:
                  </label>
                  <select
                    name="rol"
                    value={nuevoUsuario.rol}
                    onChange={handleInputChange}
                    style={customStyles.input}
                  >
                    {/* Los values se mantienen tal cual para tu lógica, solo traducimos las etiquetas */}
                    <option value="Ventas">{t("roles.Ventas", "Sales")}</option>
                    <option value="Compras">
                      {t("roles.Compras", "Purchasing")}
                    </option>
                    <option value="Contabilidad">
                      {t("roles.Contabilidad", "Accounting")}
                    </option>
                    <option value="Administrador">
                      {t("roles.Administrador", "Administrator")}
                    </option>
                  </select>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      gap: "10px",
                      marginTop: "30px",
                    }}
                  >
                    <button
                      onClick={handleSaveUser}
                      disabled={isSaving}
                      style={customStyles.button}
                    >
                      {t("actions.save", "Save")}
                    </button>
                    <button
                      onClick={handleCloseModal}
                      style={{
                        ...customStyles.button,
                        backgroundColor: "#6c757d",
                      }}
                    >
                      {t("actions.cancel", "Cancel")}
                    </button>
                  </div>
                </div>
              </Modal>
            </div>

            <div className="tabla-cotizaciones-footer">
              <div className="paginador-container">
                <Paginador
                  totalItems={totalUsuarios}
                  itemsPerPage={itemsPerPage}
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={(n) => {
                    setItemsPerPage(n);
                    setCurrentPage(1);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
